import { CopyOutlined } from '@ant-design/icons'
import { useContext } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { UsernameInitials } from 'sutra/components/UsernameInitials/UsernameInitials'
import { getInitials } from 'sutra/utils/get-initials'

import { AuthContext } from 'features/auth'
import { UserInfo } from 'types/project'

type Props = {
  viewers: UserInfo[]
  owners: UserInfo[]
  handleUnshare: (email: string) => void
  isOwner: boolean
  // dashboard has additional perms controls
  handleUpdateUserPermissionLevel?: (user: UserInfo, value: string) => void
  isAuthor?: boolean
  permissionOverrides?: Record<string, 'viewer' | 'editor'>
  onCopyPublicLink?: () => void
}

const PUBLIC_VIEWER_FULL_NAME = 'Public Viewer'

const stringToColour = (str: string): string => {
  let hash = 0
  str.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash)
  })
  let colour = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    colour += value.toString(16).padStart(2, '0')
  }
  return colour
}

const permissionOptions = [
  {
    label: 'Viewer',
    value: 'viewer',
  },
  {
    label: 'Editor',
    value: 'editor',
  },
]

export const UserList: React.FC<Props> = ({
  viewers,
  owners,
  isOwner,
  handleUnshare,
  handleUpdateUserPermissionLevel,
  isAuthor,
  permissionOverrides,
  onCopyPublicLink,
}) => {
  const authContext = useContext(AuthContext)
  const currentUserEmail = authContext?.user?.attributes?.email
  const displayRole = (user: UserInfo & { owner: boolean }): string =>
    permissionOverrides?.[user.email?.toLowerCase() ?? ''] ??
    (user.owner ? 'editor' : 'viewer')
  return (
    <Container>
      <Title>Users</Title>
      {[
        ...viewers.map((el) => ({ ...el, owner: false })),
        ...owners.map((el) => ({ ...el, owner: true })),
      ]
        .sort((a, b) => a.email.localeCompare(b.email))
        .sort((_a, b) => (b.email === currentUserEmail ? 1 : -1))
        // Dashboards has concept of a public viewer user, which exists when a public sharing link has been generated
        .sort((_a, b) => (b.full_name === PUBLIC_VIEWER_FULL_NAME ? -1 : 1))
        .map((user) => (
          <UserRow key={user.email}>
            {/* Dashboards has concept of a public viewer user, which exists when a public sharing link has been generated */}
            {user.full_name === PUBLIC_VIEWER_FULL_NAME ? (
              <UserDetails>
                <UsernameInitials
                  initials="PL"
                  size="lg"
                  background={stringToColour(user.email)}
                  color="white"
                />
                <div>
                  <Name>Shared via Public Link</Name>
                </div>
              </UserDetails>
            ) : (
              <UserDetails>
                <UsernameInitials
                  initials={getInitials({ fullName: user.full_name })}
                  size="lg"
                  background={stringToColour(user.email)}
                  color="white"
                />
                <div>
                  <NameRow>
                    <Name>
                      {user.full_name}
                      {user.email === currentUserEmail ? ' (you)' : ''}
                    </Name>
                    {!handleUpdateUserPermissionLevel &&
                      user.owner &&
                      user.full_name !== PUBLIC_VIEWER_FULL_NAME && (
                        <OwnerChiclet>Owner</OwnerChiclet>
                      )}
                  </NameRow>
                  <Email>{user.email}</Email>
                </div>
              </UserDetails>
            )}
            <UserActions>
              {handleUpdateUserPermissionLevel && (
                <>
                  {/* Dashboards has concept of a public viewer user, which exists when a public sharing link has been generated */}
                  {user.full_name !== PUBLIC_VIEWER_FULL_NAME && (
                    <Select
                      style={{ width: 80 }}
                      value={displayRole(user)}
                      options={permissionOptions}
                      // viewers can't change others permissions
                      // users can't edit their own permissions
                      // Author can always change all perms (but self)
                      disabled={
                        (!isOwner && !isAuthor) ||
                        user.email === currentUserEmail
                      }
                      onChange={(value) =>
                        handleUpdateUserPermissionLevel(user, value)
                      }
                    />
                  )}
                </>
              )}
              {user.full_name === PUBLIC_VIEWER_FULL_NAME &&
                onCopyPublicLink && (
                  <Tooltip content="Copy public link">
                    <Button
                      variant="secondary"
                      size="xs"
                      icon={<CopyOutlined />}
                      aria-label="Copy public link"
                      onClick={onCopyPublicLink}
                    />
                  </Tooltip>
                )}
              {isOwner && !user.owner && user.email !== currentUserEmail && (
                <Button
                  variant="secondary"
                  size="xs"
                  aria-label={`Remove ${user.email}`}
                  onClick={() => {
                    handleUnshare(user.email)
                  }}
                >
                  X
                </Button>
              )}
            </UserActions>
          </UserRow>
        ))}
    </Container>
  )
}

const UserRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  min-height: 35px;
`

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const UserDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

const Name = styled.div`
  font-weight: 500;
`

const Email = styled.div`
  font-size: 10px;
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
`

const Container = styled.div`
  ${UserRow}:last-child {
    margin-bottom: 0;
  }
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const OwnerChiclet = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #595959;
  background: #f0f0f0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 1px 7px;
  white-space: nowrap;
`
